import { Injectable } from '@angular/core';
import { StorageKey } from './storage-keys';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

/**
 * Unified storage service that wraps localStorage (synchronous)
 * and @capacitor/preferences (async, for native-only keys).
 *
 * Usage:
 *   - `get / set / remove` for synchronous localStorage access (majority of keys)
 *   - `getAsync / setAsync / removeAsync` for Capacitor Preferences (native push tokens, etc.)
 *
 * All key names should come from StorageKeys constants.
 */
@Injectable({ providedIn: 'root' })
export class AppStorageService {

  // ── Synchronous (localStorage) ──

  get(key: StorageKey): string | null {
    return localStorage.getItem(key);
  }

  getJSON<T = any>(key: StorageKey): T | null {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return null;
    }
  }

  set(key: StorageKey, value: string): void {
    localStorage.setItem(key, value);
  }

  setJSON(key: StorageKey, value: any): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  remove(key: StorageKey): void {
    localStorage.removeItem(key);
  }

  clear(): void {
    localStorage.clear();
  }

  // ── Async (Capacitor Preferences – native only) ──

  async getAsync(key: StorageKey): Promise<any> {
    if (!Capacitor.isNativePlatform()) {
      return this.getJSON(key);
    }
    const item = await Preferences.get({ key });
    if (item.value) {
      try {
        return JSON.parse(item.value);
      } catch {
        return item.value;
      }
    }
    return null;
  }

  async setAsync(key: StorageKey, value: any): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.setJSON(key, value);
      return;
    }
    await Preferences.set({ key, value: JSON.stringify(value) });
  }

  async removeAsync(key: StorageKey): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      this.remove(key);
      return;
    }
    await Preferences.remove({ key });
  }
}
